import { Suspense, useCallback, useState } from 'react';
import cx from 'classnames';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { togglePane } from '../../store/slices/pane';
import { selectedNodeItemSelector } from '../../store/selectors/tree/node';
import Loading from '../Loading';
import Pane from '../Pane';
import styles from './PropertiesPane.module.scss';
import PropertiesTabs from './PropertiesTabs/PropertiesTabs';
import { TabTypes } from './PropertiesTabs/PropertyTab';
import { TabNodes } from './TabNodes';
import { NodeBaseItem } from '../../entities/Node';
import { trackUIInteraction } from '../../utils/analyticsEvents';
import type { AppThunkDispatch } from '../../store';
import Cross from '@ingka/ssr-icon/paths/cross';
import Header from '../Pane/Header';
import { PANE_PROPERTIES } from '../../store/constants/panes';

const PropertiesPaneContent = ({
  nodeItem,
  closePane,
}: {
  nodeItem: NodeBaseItem;
  closePane: () => void;
}) => {
  const [activeTab, setActiveTab] = useState<TabTypes>('Details');

  return (
    <Pane id={PANE_PROPERTIES}>
      <Header
        buttons={[
          <Tooltip
            describedById="close-details-tooltip"
            interactiveCustomTrigger
            tooltipText="Close details"
            key="close-details"
            position="leading"
          >
            <Button
              key="close-details"
              size="small"
              iconOnly
              onClick={closePane}
              type="tertiary"
              ssrIcon={Cross}
              aria-label="Hide details pane"
            />
          </Tooltip>,
        ]}
      >
        <div className={cx(styles.headerContent)}>
          <div role="tablist" className="tabs__list">
            <PropertiesTabs
              node={nodeItem?.node}
              type={nodeItem?.node?.type}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
        </div>
      </Header>
      <Suspense fallback={<Loading text="Component is loading, please wait..." />}>
        <TabNodes
          activeTab={activeTab}
          key={nodeItem?.node.id ?? 'na'}
          node={nodeItem?.node}
          nodeItem={nodeItem}
          catalogId={nodeItem?.node.catalogId}
        />
      </Suspense>
    </Pane>
  );
};

export const PropertiesPane = ({ className }: { className: string }) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const nodeItem = useSelector(selectedNodeItemSelector) as NodeBaseItem;
  const closePane = useCallback(() => {
    dispatch(togglePane(false));
    trackUIInteraction('properties_pane_close');
  }, [dispatch]);

  return (
    <div className={className}>
      <PropertiesPaneContent
        key={nodeItem?.node.id}
        nodeItem={nodeItem}
        closePane={closePane}
      />
    </div>
  );
};
